'use client'

import { Button } from '@/components/ui'
import { useRouter } from 'next/navigation'
import { useState, useEffect, useRef } from 'react'
import { PageLoading } from '@/components/ui/loading-spinner'
import { safeFetch } from '@/utils/safeFetch'

type ContactType = 'bug' | 'feedback' | 'feature' | 'other'

// reCAPTCHA v3 類型定義
declare global {
  interface Window {
    grecaptcha?: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export default function ContactPage() {
  const router = useRouter()
  const [selectedType, setSelectedType] = useState<ContactType | null>(null)
  const [isClient, setIsClient] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle')
  const [submitMessage, setSubmitMessage] = useState('')
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false)
  const recaptchaSiteKey = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY
  
  // 表單狀態
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    contactInfo: '',
    pageUrl: ''
  })

  // 網路連線資訊
  const [networkInfo, setNetworkInfo] = useState<{
    connectionType?: string;
    effectiveType?: string;
    downlink?: number;
    rtt?: number;
    saveData?: boolean;
  } | null>(null)

  // 載入 reCAPTCHA v3 腳本
  useEffect(() => {
    if (!recaptchaSiteKey || typeof window === 'undefined') return;

    // 檢查是否已經載入
    if (window.grecaptcha) {
      setRecaptchaLoaded(true);
      return;
    }

    // 載入 reCAPTCHA v3 腳本
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${recaptchaSiteKey}`;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      }
    };
    script.onerror = () => {
      console.warn('[Contact] Failed to load reCAPTCHA script');
      // 即使載入失敗，也允許提交（後端會處理）
      setRecaptchaLoaded(true);
    };
    document.head.appendChild(script);

    return () => {
      // 清理腳本（可選）
      const existingScript = document.querySelector(`script[src*="recaptcha"]`);
      if (existingScript) {
        // 不刪除，因為可能被其他頁面使用
      }
    };
  }, [recaptchaSiteKey]);

  // 確保在客戶端渲染並收集資訊
  useEffect(() => {
    setIsClient(true)
    // 自動偵測當前頁面網址
    if (typeof window !== 'undefined') {
      setFormData(prev => ({
        ...prev,
        pageUrl: window.location.href
      }))

      // 選項 8：收集網路連線資訊
      const collectNetworkInfo = () => {
        try {
          // 檢查是否支援 Network Information API
          const connection = (navigator as any).connection || 
                           (navigator as any).mozConnection || 
                           (navigator as any).webkitConnection;
          
          if (connection) {
            setNetworkInfo({
              connectionType: connection.type || connection.effectiveType || '未知',
              effectiveType: connection.effectiveType || '未知',
              downlink: connection.downlink,
              rtt: connection.rtt,
              saveData: connection.saveData
            })
          } else {
            // 不支援 Network Information API
            setNetworkInfo(null)
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Failed to collect network info:', error)
          }
          setNetworkInfo(null)
        }
      }

      // 立即收集一次
      collectNetworkInfo()

      // 監聽連線變化（可選）
      const connection = (navigator as any).connection || 
                        (navigator as any).mozConnection || 
                        (navigator as any).webkitConnection;
      
      if (connection) {
        connection.addEventListener('change', collectNetworkInfo)
        return () => {
          connection.removeEventListener('change', collectNetworkInfo)
        }
      }
    }
  }, [])

  const contactTypes = [
    {
      type: 'bug' as ContactType,
      title: '錯誤報修',
      description: '發現網站有問題或功能異常嗎？',
      emoji: '🐛',
      color: 'bg-red-500 hover:bg-red-600'
    },
    {
      type: 'feedback' as ContactType,
      title: '意見反應',
      description: '分享您使用網站的心得與建議',
      emoji: '💬',
      color: 'bg-blue-500 hover:bg-blue-600'
    },
    {
      type: 'feature' as ContactType,
      title: '功能許願',
      description: '希望增加什麼新功能？告訴我們！',
      emoji: '✨',
      color: 'bg-purple-500 hover:bg-purple-600'
    },
    {
      type: 'other' as ContactType,
      title: '其他',
      description: '其他任何想聯絡我們的事項',
      emoji: '📧',
      color: 'bg-gray-500 hover:bg-gray-600'
    }
  ]

  const handleTypeSelect = (type: ContactType | '') => {
    setSelectedType(type || null)
    setSubmitStatus('idle')
    setSubmitMessage('')
    // 切換類型時清空標題和內容，讓用戶重新填寫
    setFormData(prev => ({
      ...prev,
      title: '',
      message: ''
    }))
  }

  // 根據類型獲取標題提示
  const getTitlePlaceholder = (type: ContactType | null): string => {
    if (!type) return '請先選擇聯絡類型'
    switch (type) {
      case 'bug':
        return '例如：N5 單字頁面無法翻頁'
      case 'feedback':
        return '例如：整體使用體驗很好，但希望能...'
      case 'feature':
        return '例如：希望新增單字收藏功能'
      case 'other':
        return '請輸入標題'
      default:
        return '請輸入標題'
    }
  }

  // 根據類型獲取內容提示
  const getMessagePlaceholder = (type: ContactType | null): string => {
    if (!type) return '請先選擇聯絡類型'
    switch (type) {
      case 'bug':
        return '請描述您遇到的問題：\n\n1. 問題發生時您正在做什麼？\n2. 問題的具體表現（例如：按鈕無法點擊、頁面顯示錯誤等）\n3. 問題是否可以重現？'
      case 'feedback':
        return '請分享您的使用心得與建議：\n\n例如：整體使用體驗、哪些功能好用、哪些地方需要改進等'
      case 'feature':
        return '請描述您希望新增的功能：\n\n1. 功能名稱：\n2. 功能用途：\n3. 為什麼需要這個功能？'
      case 'other':
        return '請輸入您的訊息...'
      default:
        return '請輸入訊息內容'
    }
  }

  // 根據類型獲取標題標籤文字
  const getTitleLabel = (type: ContactType | null): string => {
    if (!type) return '標題'
    switch (type) {
      case 'bug':
        return '問題標題'
      case 'feedback':
        return '意見標題'
      case 'feature':
        return '功能名稱'
      case 'other':
        return '標題'
      default:
        return '標題'
    }
  }

  // 根據類型獲取內容標籤文字
  const getMessageLabel = (type: ContactType | null): string => {
    if (!type) return '訊息內容'
    switch (type) {
      case 'bug':
        return '問題描述'
      case 'feedback':
        return '意見內容'
      case 'feature':
        return '功能描述'
      case 'other':
        return '訊息內容'
      default:
        return '訊息內容'
    }
  }

  const handleInputChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    setSubmitStatus('idle')
    setSubmitMessage('')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!selectedType) {
      setSubmitStatus('error')
      setSubmitMessage('請選擇聯絡類型')
      return
    }

    if (!formData.title.trim()) {
      setSubmitStatus('error')
      setSubmitMessage('請填寫標題')
      return
    }

    if (!formData.message.trim()) {
      setSubmitStatus('error')
      setSubmitMessage('請填寫訊息內容')
      return
    }

    setIsSubmitting(true)
    setSubmitStatus('idle')
    setSubmitMessage('')

    try {
      // 獲取 reCAPTCHA v3 token（如果已配置）
      let recaptchaToken: string | undefined = undefined;
      if (recaptchaSiteKey && window.grecaptcha && recaptchaLoaded) {
        try {
          recaptchaToken = await window.grecaptcha.execute(recaptchaSiteKey, {
            action: 'contact_submit'
          });
        } catch (error) {
          console.warn('[Contact] Failed to get reCAPTCHA token:', error);
          // 繼續提交，後端會處理
        }
      }

      const response = await safeFetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: selectedType,
          title: formData.title.trim(),
          message: formData.message.trim(),
          contactInfo: formData.contactInfo.trim() || undefined,
          pageUrl: formData.pageUrl.trim() || undefined,
          recaptchaToken: recaptchaToken,
          networkInfo: networkInfo || undefined
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || data.error || '發送失敗')
      }

      // 成功
      setSubmitStatus('success')
      setSubmitMessage(data.message || '您的訊息已成功送出，我們會在 1-3 個工作天內回覆您')
      
      // 清空表單
      setFormData({
        title: '',
        message: '',
        contactInfo: '',
        pageUrl: window.location.href
      })
      setSelectedType(null)

      // 3 秒後重置狀態
      setTimeout(() => {
        setSubmitStatus('idle')
        setSubmitMessage('')
      }, 5000)

    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Submit contact form error:', error)
      }
      setSubmitStatus('error')
      setSubmitMessage(
        error instanceof Error 
          ? error.message 
          : '發送訊息時發生錯誤，請稍後再試或直接發送郵件至 ' + (process.env.NEXT_PUBLIC_CONTACT_EMAIL || 'support@jlpt-aitutor.com')
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  // 顯示載入畫面
  if (!isClient) {
    return <PageLoading text="載入中..." />
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* 頂部導航 */}
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">聯絡我們</h1>
          <Button
            variant="secondary"
            onClick={() => router.back()}
          >
            返回
          </Button>
        </div>

        {/* 說明文字 */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <p className="text-lg text-gray-300 mb-2">
            感謝您使用 JLPT 學習平台！
          </p>
          <p className="text-lg text-gray-300 mb-4">
            我們非常重視您的意見與回饋。
          </p>
          <p className="text-gray-400 mb-2">
            我們將在 3-5 個工作天內回覆您。
          </p>
        </div>

        {/* 表單 */}
        <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6 mb-8">
          {/* 聯絡類型選擇 - 下拉選單 */}
          <div className="mb-6">
            <label htmlFor="contactType" className="block text-sm font-medium text-gray-300 mb-2">
              聯絡類型 <span className="text-red-400">*</span>
            </label>
            <select
              id="contactType"
              value={selectedType || ''}
              onChange={(e) => handleTypeSelect(e.target.value as ContactType | '')}
              required
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">請選擇聯絡類型</option>
              {contactTypes.map((item) => (
                <option key={item.type} value={item.type}>
                  {item.emoji} {item.title} - {item.description}
                </option>
              ))}
            </select>
            {selectedType && (
              <p className="text-sm text-gray-400 mt-2">
                {contactTypes.find(t => t.type === selectedType)?.description}
              </p>
            )}
          </div>

          {/* 根據類型顯示不同的填寫框 */}
          {selectedType && (
            <>
              {/* 標題 */}
              <div className="mb-6">
                <label htmlFor="title" className="block text-sm font-medium text-gray-300 mb-2">
                  {getTitleLabel(selectedType)} <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  id="title"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder={getTitlePlaceholder(selectedType)}
                  maxLength={200}
                  required
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.title.length}/200</p>
              </div>

              {/* 訊息內容 */}
              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  {getMessageLabel(selectedType)} <span className="text-red-400">*</span>
                </label>
                <textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => handleInputChange('message', e.target.value)}
                  placeholder={getMessagePlaceholder(selectedType)}
                  rows={10}
                  maxLength={2000}
                  required
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
                <p className="text-xs text-gray-500 mt-1">{formData.message.length}/2000</p>
              </div>
            </>
          )}

          {/* 如果未選擇類型，顯示提示 */}
          {!selectedType && (
            <div className="mb-6 p-4 bg-gray-700/50 border border-gray-600 rounded-lg text-center text-gray-400">
              <p>請先選擇聯絡類型以開始填寫表單</p>
            </div>
          )}

          {/* 聯絡方式（選填）- 只在選擇類型後顯示 */}
          {selectedType && (
            <div className="mb-6">
              <label htmlFor="contactInfo" className="block text-sm font-medium text-gray-300 mb-2">
                聯絡方式（選填）
              </label>
              <input
                type="text"
                id="contactInfo"
                value={formData.contactInfo}
                onChange={(e) => handleInputChange('contactInfo', e.target.value)}
                placeholder="Email 或電話號碼（方便我們回覆您）"
                maxLength={500}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* 狀態訊息 */}
          {submitStatus !== 'idle' && (
            <div className={`mb-6 p-4 rounded-lg ${
              submitStatus === 'success' 
                ? 'bg-green-900/50 border border-green-500 text-green-300'
                : 'bg-red-900/50 border border-red-500 text-red-300'
            }`}>
              {submitMessage}
            </div>
          )}

          {/* 提交按鈕 */}
          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setSelectedType(null)
                setFormData({
                  title: '',
                  message: '',
                  contactInfo: '',
                  pageUrl: window.location.href
                })
                setSubmitStatus('idle')
                setSubmitMessage('')
              }}
              disabled={isSubmitting}
            >
              清除
            </Button>
            <Button
              type="submit"
              variant="default"
              disabled={isSubmitting || !selectedType || !formData.title.trim() || !formData.message.trim()}
              className="bg-blue-500 hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed min-w-[100px]"
            >
              {isSubmitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  發送中...
                </span>
              ) : (
                '送出'
              )}
            </Button>
          </div>
        </form>

        {/* 其他聯絡方式 */}
        <div className="bg-gray-800 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold mb-4">其他聯絡方式</h2>
          <div className="space-y-3 text-gray-300">
            <div className="flex items-center gap-3">
              <span className="text-2xl">📧</span>
              <div>
                <p className="font-semibold">電子郵件</p>
                <a 
                  href={`mailto:${process.env.NEXT_PUBLIC_CONTACT_EMAIL || 'support@jlpt-aitutor.com'}`}
                  className="text-blue-400 hover:text-blue-300 transition-colors"
                >
                  {process.env.NEXT_PUBLIC_CONTACT_EMAIL || 'support@jlpt-aitutor.com'}
                </a>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-2xl">⏰</span>
              <div>
                <p className="font-semibold">回覆時間</p>
                <p className="text-gray-400">我們會在 1-3 個工作天內回覆您</p>
              </div>
            </div>
          </div>
        </div>

        {/* 公司資訊說明 */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-6">
          <h3 className="text-lg font-bold mb-2 flex items-center gap-2">
            <span>ℹ️</span>
            公司資訊
          </h3>
          <div className="space-y-2 text-gray-300">
            <div className="text-sm">
              君王資訊科技有限公司
            </div>
            <div className="text-sm">
              統一編號：45124174
            </div>
            <div className="text-sm">
              信箱：<a 
                href="mailto:king.protee@gmail.com" 
                className="text-blue-400 hover:text-blue-300 transition-colors"
              >
                king.protee@gmail.com
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
